import PageMeta from "../components/PageMeta";
import ListingResultsView from "../components/ListingResultsView";
import ListingSort from "../components/ListingSort";
import uiI18n from "../i18n";
import SaveSearchPrompt from "../components/SaveSearchPrompt";
import { useLocation, useNavigate } from "react-router-dom";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

import {
  LuChevronDown,
  LuMap,
  LuMapPin,
  LuRefreshCcw,
  LuSearch,
  LuUmbrella,
  LuUsers,
} from "react-icons/lu";

import citiesAndDelegations from "../data/citiesAndDelegations.js";
import Spinner from "../components/Spinner";
import ListVacation from "../components/ListVacation.jsx";
import { getVacations } from "../Services/vacationsService";

import VacationPriceFilter from "../components/VacationPriceFilter";
import { POPULAR_DESTINATIONS } from "../data/popularDestinations";

const PAGE_SIZE = 20;

// Stay dates travel as YYYY-MM-DD (local calendar day); invalid or partial stays are ignored.
const isoDay = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const validStay = (checkIn, checkOut) => /^\d{4}-\d{2}-\d{2}$/.test(checkIn || "") && /^\d{4}-\d{2}-\d{2}$/.test(checkOut || "") && checkOut > checkIn;

export default function Vacation() {
  // ?view=map opens the results directly on the map (mobile "Voir sur la carte").
  const [resultsView, setResultsView] = useState(() => new URLSearchParams(window.location.search).get("view") === "map" ? "map" : "list");
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const requestId = useRef(0);
  const query = new URLSearchParams(location.search);
  const minPrice = query.get("minPrice") ?? "";
  const maxPrice = query.get("maxPrice") ?? "";

  const [destination, setDestination] =
    useState("");
  const [phoneFilters, setPhoneFilters] = useState(false);
  const [keyword,setKeyword] = useState("");
  const [travelers, setTravelers] =
    useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const resultsRef = useRef(null);

  const [vacations, setVacations] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadingMore, setLoadingMore] =
    useState(false);

  const [error, setError] = useState("");
  // Offered after each search, saved only if the visitor accepts (SaveSearchPrompt).
  const [searchOffer, setSearchOffer] = useState(null);
  const [hasMore, setHasMore] =
    useState(false);

  const [page, setPage] = useState(0);

  const fetchVacations = useCallback(
    async ({
      pageNumber = 0,
      replace = true,
      city = "",
      guests = "",
      text = "",
      minPrice = "",
      maxPrice = "",
      sort = "",
      stayStart = "",
      stayEnd = "",
    } = {}) => {
      const currentRequest = ++requestId.current;
      if (replace) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      setError("");

      try {
        const filters = {
          city,
          travelers: guests,
          minPrice,
          maxPrice,
          sort,
          ...(validStay(stayStart, stayEnd) ? { checkIn: stayStart, checkOut: stayEnd } : {}),
        };

        const response = await getVacations(
          pageNumber,
          filters
        );

        if (currentRequest !== requestId.current) return;
        const normalizedResponse =
          Array.isArray(response)
            ? response.filter(item => !text || [item.libelle,item.ville,item.delegation].some(value => String(value||"").toLowerCase().includes(text.toLowerCase())))
            : [];

        if (replace && pageNumber === 0) {
          setSearchOffer({ kind: 'vacation', city, travelers: guests, query: text });
        }

        setVacations((previousVacations) =>
          replace
            ? normalizedResponse
            : [
                ...previousVacations,
                ...normalizedResponse,
              ]
        );

        setPage(pageNumber);

        setHasMore(
          Array.isArray(response) && response.length === PAGE_SIZE
        );
      } catch (requestError) {
        if (currentRequest !== requestId.current) return;
        console.error(
          "Erreur lors du chargement des locations :",
          requestError
        );

        setError(
          requestError?.message ||
            t(
              "errorFetchingVacations",
              "Impossible de charger les locations de vacances."
            )
        );

        if (replace) {
          setVacations([]);
        }
      } finally {
        if (currentRequest === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [t]
  );

  useEffect(() => {
    const query=new URLSearchParams(location.search);
    const city=query.get('city')||'', guests=query.get('travelers')||'';
    setDestination(city); setTravelers(guests);
    const text=query.get("query")||query.get("libelle")||"";setKeyword(text);
    const stayStart=query.get("checkIn")||"", stayEnd=query.get("checkOut")||"";
    setCheckIn(stayStart); setCheckOut(stayEnd);
    fetchVacations({city,guests,text,minPrice:query.get("minPrice") ?? "",maxPrice:query.get("maxPrice") ?? "",sort:query.get("sort") || "",stayStart,stayEnd});
  }, [fetchVacations,location.search]);

  const applyQuery = next => {
    if (next.toString() === query.toString()) {
      fetchVacations({ city: next.get('city') || '', guests: next.get('travelers') || '', text: next.get('query') || next.get('libelle') || '', minPrice: next.get('minPrice') ?? '', maxPrice: next.get('maxPrice') ?? '', sort: next.get('sort') || '', stayStart: next.get('checkIn') || '', stayEnd: next.get('checkOut') || '' });
    } else navigate({ pathname: location.pathname, search: next.toString() });
  };
  const handleSearch = event => {
    event.preventDefault();
    const next = new URLSearchParams(location.search);
    next.delete('libelle'); next.delete('page');
    Object.entries({city: destination, travelers, query: keyword}).forEach(([key,value]) => {
      if (value !== '') next.set(key,value); else next.delete(key);
    });
    // Only a complete, ordered stay filters availability.
    if (validStay(checkIn, checkOut)) { next.set('checkIn', checkIn); next.set('checkOut', checkOut); }
    else { next.delete('checkIn'); next.delete('checkOut'); }
    applyQuery(next);
  };
  const searchDestination = city => {
    setDestination(city);
    const next = new URLSearchParams(location.search);
    next.delete('page'); next.set('city', city);
    applyQuery(next);
  };
  const showMap = () => {
    setResultsView('map');
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const handlePriceApply = (minimum, maximum) => {
    const next = new URLSearchParams(location.search);
    next.delete('page');
    Object.entries({minPrice: minimum, maxPrice: maximum}).forEach(([key,value]) => {
      if (value !== '') next.set(key,value); else next.delete(key);
    });
    applyQuery(next);
  };
  const handleReset = () => {
    setDestination(''); setKeyword(''); setTravelers(''); setCheckIn(''); setCheckOut('');
    const next = new URLSearchParams(location.search);
    ['city','travelers','query','libelle','minPrice','maxPrice','page','sort','checkIn','checkOut'].forEach(key => next.delete(key));
    applyQuery(next);
  };
  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    fetchVacations({ pageNumber: page + 1, replace: false, city: query.get('city') || '', guests: query.get('travelers') || '', text: query.get('query') || query.get('libelle') || '', minPrice, maxPrice, sort: query.get('sort') || '', stayStart: query.get('checkIn') || '', stayEnd: query.get('checkOut') || '' });
  };

  const handleTravelersChange = (event) => {
    const value = event.target.value;

    if (value === "") {
      setTravelers("");
      return;
    }

    const parsedValue = Number(value);

    if (
      Number.isInteger(parsedValue) &&
      parsedValue >= 1 &&
      parsedValue <= 20
    ) {
      setTravelers(parsedValue);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50">
      <PageMeta titleKey="seo.vacation" path="/Vacation" />
      {/* En-tête */}
      <section className="lina-vacation-hero border-b border-slate-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
          <div className="mx-auto max-w-3xl text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-600">
              <LuUmbrella size={27} />
            </span>

            <p className="mt-5 text-xs font-black uppercase tracking-[0.2em] text-green-600"> {uiI18n.t("ui.lina_immo_holidays")} </p>

            <h1 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl">
              {t(
                "location_Vacation",
                "Trouvez votre location de vacances"
              )}
            </h1>

            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
              {t(
                "vacation.search_description",
                "Découvrez des logements disponibles partout en Tunisie pour vos vacances et courts séjours."
              )}
            </p>
          </div>

          {/* Recherche */}
          <form
            onSubmit={handleSearch}
            data-phone-filters={phoneFilters || undefined}
            className="
              lina-vacation-search
              mx-auto
              mt-8
              grid
              max-w-5xl
              grid-cols-1
              gap-4
              rounded-3xl
              border
              border-slate-200
              bg-white
              p-4
              shadow-xl
              shadow-slate-900/5
              sm:p-5
              md:grid-cols-2
              lg:grid-cols-3
            "
          >
            <button type="button" className="phone-only phone-vacation-filters" aria-expanded={phoneFilters} onClick={() => setPhoneFilters(v => !v)}>{t("mobile.filters")}</button>
            <button type="submit" className="phone-only phone-vacation-submit" aria-label={t("search_form.search")}><LuSearch aria-hidden="true" /></button>
            <label className="lina-vs-keyword flex flex-col gap-2">{uiI18n.t("ui.keyword")} <input className="rounded-xl border p-3" value={keyword} onChange={event=>setKeyword(event.target.value)} placeholder={uiI18n.t("ui.title_or_location")} />
            </label>
            {/* Destination */}
            <div className="lina-vs-destination">
              <label
                htmlFor="destination"
                className="mb-2 block text-sm font-black text-slate-700"
              >
                {t(
                  "destination",
                  "Destination"
                )}
              </label>

              <div className="relative">
                <LuMapPin
                  size={18}
                  className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <select
                  id="destination"
                  value={destination}
                  onChange={(event) =>
                    setDestination(
                      event.target.value
                    )
                  }
                  className="
                    h-12
                    w-full
                    appearance-none
                    rounded-2xl
                    border
                    border-slate-200
                    bg-slate-50
                    ps-11
                    pe-10
                    text-sm
                    font-medium
                    text-slate-900
                    outline-none
                    transition
                    hover:border-slate-300
                    focus:border-green-500
                    focus:bg-white
                    focus:ring-4
                    focus:ring-green-500/10
                  "
                >
                  <option value="">
                    {t(
                      "city",
                      "Toutes les villes"
                    )}
                  </option>

                  {Object.keys(
                    citiesAndDelegations || {}
                  ).map((city) => (
                    <option
                      key={city}
                      value={city}
                    >
                      {t(
                        `cities.${city}`,
                        city
                      )}
                    </option>
                  ))}
                </select>

                <LuChevronDown
                  size={16}
                  className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-slate-400"
                />
              </div>
            </div>

            {/* Mobile : séjour (arrivée / départ) — filtre de disponibilité côté serveur. */}
            <div className="lina-vs-in lina-m-only">
              <label htmlFor="vacation-check-in">{t("mobile.arrival")}</label>
              <input id="vacation-check-in" type="date" value={checkIn} min={isoDay(new Date())}
                onChange={event => { setCheckIn(event.target.value); if (checkOut && event.target.value >= checkOut) setCheckOut(""); }} />
            </div>
            <div className="lina-vs-out lina-m-only">
              <label htmlFor="vacation-check-out">{t("mobile.departure")}</label>
              <input id="vacation-check-out" type="date" value={checkOut}
                min={checkIn ? isoDay(new Date(Number(checkIn.slice(0, 4)), Number(checkIn.slice(5, 7)) - 1, Number(checkIn.slice(8, 10)) + 1)) : isoDay(new Date())}
                onChange={event => setCheckOut(event.target.value)} />
            </div>

            {/* Voyageurs */}
            <div className="lina-vs-travelers">
              <label
                htmlFor="travelers"
                className="mb-2 block text-sm font-black text-slate-700"
              >
                {t(
                  "travelers",
                  "Voyageurs"
                )}
              </label>

              <div className="relative">
                <LuUsers
                  size={18}
                  className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  id="travelers"
                  type="number"
                  value={travelers}
                  min="1"
                  max="20"
                  inputMode="numeric"
                  onChange={
                    handleTravelersChange
                  }
                  placeholder={t(
                    "vacation.guests_placeholder",
                    "Nombre"
                  )}
                  className="
                    h-12
                    w-full
                    rounded-2xl
                    border
                    border-slate-200
                    bg-slate-50
                    ps-11
                    pe-4
                    text-sm
                    font-medium
                    text-slate-900
                    outline-none
                    transition
                    placeholder:text-slate-400
                    hover:border-slate-300
                    focus:border-green-500
                    focus:bg-white
                    focus:ring-4
                    focus:ring-green-500/10
                  "
                />
              </div>
            </div>

            <VacationPriceFilter minPrice={minPrice} maxPrice={maxPrice} onApply={handlePriceApply} />

            {/* Rechercher */}
            <div className="lina-vs-submit flex items-end">
              <button
                type="submit"
                disabled={loading}
                className="
                  inline-flex
                  h-12
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-2xl
                  bg-green-600
                  px-6
                  text-sm
                  font-black
                  text-white
                  shadow-lg
                  shadow-green-600/20
                  transition-all
                  hover:-translate-y-0.5
                  hover:bg-green-700
                  disabled:cursor-not-allowed
                  disabled:bg-slate-300
                  disabled:shadow-none
                  focus:outline-none
                  focus:ring-4
                  focus:ring-green-500/20
                  lg:w-auto
                "
              >
                <LuSearch size={18} />

                {t(
                  "search_form.search",
                  "Rechercher"
                )}
              </button>
            </div>

            {/* Réinitialiser */}
            <div className="lina-vs-reset flex items-end">
              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className="
                  inline-flex
                  h-12
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white
                  px-5
                  text-sm
                  font-bold
                  text-slate-600
                  transition
                  hover:border-slate-300
                  hover:bg-slate-100
                  hover:text-slate-900
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                  focus:outline-none
                  focus:ring-4
                  focus:ring-slate-500/10
                  lg:w-auto
                "
              >
                <LuRefreshCcw size={17} />

                {t(
                  "search_form.reset",
                  "Réinitialiser"
                )}
              </button>
            </div>
          </form>

          {/* Mobile : destinations populaires et carte, juste sous la recherche. */}
          <nav className="lina-m-popular sm:hidden" aria-label={t("mobile.popular")}>
            <h2>{t("mobile.popular")}</h2>
            <div>
              {POPULAR_DESTINATIONS.map(({ label, city }) => (
                <button key={label} type="button" aria-pressed={destination === city} onClick={() => searchDestination(city)}>{label}</button>
              ))}
            </div>
          </nav>
          <button type="button" className="lina-m-secondary lina-vacation-map sm:hidden" onClick={showMap}>
            <LuMap aria-hidden="true" size={20} />
            {t("mobile.viewMap")}
          </button>
        </div>
        {!loading && vacations.length === 0 && hasMore && (
          <button type="button" disabled={loadingMore} onClick={handleLoadMore} className="mt-5 rounded-xl border bg-white px-5 py-3">
            {loadingMore ? uiI18n.t("ui.loading_218") : uiI18n.t("ui.continue_to_the_next_listings")}
          </button>
        )}
      </section>

      {/* Résultats */}
      <section ref={resultsRef} className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        {/* En-tête résultats */}
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.16em] text-green-600">
              {t(
                "vacation.available_stays",
                "Séjours disponibles"
              )}
            </p>

            <h2 className="mt-2 text-2xl font-black text-slate-950">
              {destination
                ? t(
                    "vacation.results_in_city",
                    "Locations à {{city}}",
                    {
                      city: t(
                        `cities.${destination}`,
                        destination
                      ),
                    }
                  )
                : t(
                    "vacation.all_results",
                    "Toutes les locations"
                  )}
            </h2>
          </div>

          {!loading && vacations.length > 0 && (
            <p className="text-sm font-semibold text-slate-500">
              {vacations.length}{" "}
              {t(
                vacations.length > 1
                  ? "vacation.results"
                  : "vacation.result",
                vacations.length > 1
                  ? "résultats"
                  : "résultat"
              )}
            </p>
          )}
        </div>

        <ListingSort vacation value={query.get('sort')} onChange={value => {
          const next = new URLSearchParams(location.search); next.delete('page');
          if (value === 'recent') next.delete('sort'); else next.set('sort', value);
          applyQuery(next);
        }} />
        {/* Erreur */}
        {error && (
          <div
            role="alert"
            className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          >
            {error}
          </div>
        )}
        <SaveSearchPrompt search={searchOffer} />

        {/* Chargement */}
        {loading ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <Spinner />
          </div>
        ) : vacations.length > 0 ? (
          <>
            <ListingResultsView listings={vacations} kind="vacation" Card={ListVacation} mode={resultsView} onModeChange={setResultsView}>
            {visibleListings => <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visibleListings.map(
                (vacation, index) => {
                  const vacationId =
                    vacation?.id ??
                    vacation?.ID ??
                    vacation?.vacationID ??
                    index;

                  return (
                    <ListVacation
                      key={vacationId}
                      listing={vacation}
                    />
                  );
                }
              )}
            </ul>}
            </ListingResultsView>

            {/* Voir plus */}
            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="
                    inline-flex
                    h-12
                    items-center
                    justify-center
                    gap-2
                    rounded-2xl
                    border
                    border-slate-200
                    bg-white
                    px-7
                    text-sm
                    font-black
                    text-slate-700
                    shadow-sm
                    transition-all
                    hover:-translate-y-0.5
                    hover:border-green-300
                    hover:bg-green-50
                    hover:text-green-700
                    hover:shadow-md
                    disabled:cursor-not-allowed
                    disabled:opacity-60
                    focus:outline-none
                    focus:ring-4
                    focus:ring-green-500/15
                  "
                >
                  {loadingMore ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-green-600" />

                      {t(
                        "loading",
                        "Chargement..."
                      )}
                    </>
                  ) : (
                    <>
                      <LuRefreshCcw size={17} />

                      {t(
                        "see_plus",
                        "Voir plus"
                      )}
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          /* Aucun résultat */
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-5 py-16 text-center shadow-sm">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-50 text-green-600">
              <LuUmbrella size={30} />
            </span>

            <h3 className="mt-5 text-xl font-black text-slate-900">
              {t(
                "no_exact_match",
                "Aucune location trouvée"
              )}
            </h3>

            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
              {t(
                "modify_filters",
                "Modifiez votre destination ou le nombre de voyageurs pour découvrir plus de logements."
              )}
            </p>

            <button
              type="button"
              onClick={handleReset}
              className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 text-sm font-bold text-white transition hover:bg-green-600"
            >
              <LuRefreshCcw size={17} />

              {t(
                "search_form.reset",
                "Réinitialiser les filtres"
              )}
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
